import {useEffect, useMemo} from 'react';
import {useAppDispatch, useAppSelector, useOverridableFeatureFlags} from '../../../hooks';
import {actions} from '../../../data/timeslots/actions';
import {getWorkspaceAvailability, OfficeWorkdayParams} from '../../../data/timeslots/selectors';
import {WorkspaceReservation} from '../../../data/timeslots/apiTypes';
import {getNodeInformationByNodeId} from '../../nodeInformation';

export const useBookedCountForReservation = (workspaceReservation: WorkspaceReservation) => {
  const timeslotsEnabled = !!useOverridableFeatureFlags().TimeslotsApi;

  const aggregateId =
    'areaId' in workspaceReservation.workspace
      ? workspaceReservation.workspace.areaId
      : 'floorId' in workspaceReservation.workspace
      ? workspaceReservation.workspace.floorId
      : workspaceReservation.workspace.buildingId;

  // V1 implementation
  const selectorProps = useMemo(
    () => ({
      nodeId: aggregateId,
      date: new Date(workspaceReservation.localStart),
    }),
    [workspaceReservation.localStart, aggregateId],
  );

  const areaInformation = useAppSelector((state) => getNodeInformationByNodeId(state, selectorProps));

  // V2
  const counts = useWorkplaceReservationCounts({
    localStart: workspaceReservation.localStart,
    localEnd: workspaceReservation.localEnd,
    buildingId: workspaceReservation.workspace.buildingId,
  });

  if (timeslotsEnabled) {
    return counts?.[aggregateId] ?? null;
  } else {
    return {booked: areaInformation.occupation};
  }
};

export const useWorkplaceReservationCounts = (workday: OfficeWorkdayParams | null) => {
  const dispatch = useAppDispatch();
  const entry = useAppSelector((state) => getWorkspaceAvailability(state, workday));
  const timeslotsEnabled = !!useOverridableFeatureFlags().TimeslotsApi;

  useEffect(
    function ensureAvailabilityIsLoaded() {
      // Don't dispatch v2 request for data if ff is disabled
      if (!timeslotsEnabled) {
        // TODO: if we get here, we're not showing correct availabiltiy info to our users.
        //       To fix it, we should implement a v1 resolution OR stop using this hook in non _timelost components.
        return;
      }

      if (workday && !entry) {
        dispatch(actions.fetchWorkspaceReservationCountsForWorkday({workday}));
      }
    },
    [entry, workday, timeslotsEnabled],
  );

  return entry?.status === 'fulfilled' ? entry.data.countByNodeId : null;
};
